(() => {
  const log = (label) => {
    const ms = performance.now();
    console.log(`[perf] ${label}: ${ms.toFixed(1)}ms`);
  };

  if (document.readyState === 'interactive' || document.readyState === 'complete') {
    log('DOMContentLoaded (late attach)');
  } else {
    document.addEventListener('DOMContentLoaded', () => log('DOMContentLoaded'));
  }

  if (document.readyState === 'complete') {
    log('load (late attach)');
  } else {
    window.addEventListener('load', () => log('load'));
  }
})();

const enableAutoplayOnVideos = () => {
  const videos = document.querySelectorAll('video');
  if (!videos.length) return;

  videos.forEach((video) => {
    video.autoplay = true;
    video.muted = true;
    video.playsInline = true;

    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {});
    }
  });
};

// Number of birds in the simulation
const NUM_BOIDS = 1250;

// Boid parameters
const BOID_SIZE = 1.5;
const MAX_SPEED = 2.0;
const MAX_FORCE = 0.03;

// Flocking behavior parameters
const PERCEPTION_RADIUS = 25;
const SEPARATION_RADIUS = 25;
const PERCEPTION_RADIUS_SQ = PERCEPTION_RADIUS * PERCEPTION_RADIUS;
const SEPARATION_RADIUS_SQ = SEPARATION_RADIUS * SEPARATION_RADIUS;

// Maximum pull force towards the center
const MAX_PULL_FORCE = 0.03; // Maximum force that can be applied towards the center

const INV_PHI = (Math.sqrt(5) - 1) / 2; // Approximately 0.618

let boids = [];
let center; // Global center vector

function setup() {
  createCanvas(windowWidth, windowHeight);
  // Initialize the global center vector based on the golden ratio
  center = createVector(width * INV_PHI, height * (1 - INV_PHI));
  // Initialize boids with random positions and velocities
  for (let i = 0; i < NUM_BOIDS; i++) {
    boids.push(new Boid());
  }
  background(255, 255, 254);
  strokeWeight(BOID_SIZE);
  stroke(66);
  console.log('Boids sketch rendered and running');
  initScrollBehaviors();
  // Canvas is ready and scroll observers are attached; enable autoplay on videos.
  enableAutoplayOnVideos();
}

function draw() {
  background(255, 255, 254); // Clear the background
  for (let boid of boids) {
    boid.flock(boids);
    boid.update();
    boid.show();
  }
}

// Set up scroll-driven UI behaviors (background shift, hr fade, inquire fade).
function initScrollBehaviors() {
  const scrollHandlers = [];
  const resizeHandlers = [];

  const state = {
    scrollY: window.scrollY || window.pageYOffset,
    viewportHeight: window.innerHeight || document.documentElement.clientHeight,
  };

  let scrollTicking = false;

  const runScrollHandlers = () => {
    scrollTicking = false;
    state.scrollY = window.scrollY || window.pageYOffset;
    scrollHandlers.forEach((fn) => fn(state));
  };

  const requestScrollTick = () => {
    if (!scrollTicking) {
      scrollTicking = true;
      window.requestAnimationFrame(runScrollHandlers);
    }
  };

  const runResizeHandlers = () => {
    state.viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    resizeHandlers.forEach((fn) => fn(state));
    requestScrollTick();
  };

  const onScroll = (fn) => {
    if (typeof fn === 'function') {
      scrollHandlers.push(fn);
    }
  };

  const onResize = (fn) => {
    if (typeof fn === 'function') {
      resizeHandlers.push(fn);
    }
  };

  window.addEventListener('scroll', requestScrollTick, { passive: true });
  window.addEventListener('resize', runResizeHandlers);

  // Background color shift based on projects position.
  const projects = document.querySelectorAll('.project');
  if (projects.length) {
    const startBgColor = { r: 255, g: 255, b: 254 };
    const endBgColor = { r: 250, g: 249, b: 247 };
    const first = projects[0];
    const last = projects[projects.length - 1];

    let windowHeight = window.innerHeight;
    let firstOffsetTarget = 1;
    let lastPaintedColor = '';

    let firstTopTouched = false;
    let lastBottomTouched = false;
    let lastBottomEnteredViewport = false;

    const clamp01 = (value) => Math.min(Math.max(value, 0), 1);

    const recalcTargets = () => {
      const firstRect = first.getBoundingClientRect();
      firstOffsetTarget = Math.max(firstRect.top + window.scrollY - 140, 1);
    };

    const updateBackground = ({ viewportHeight, scrollY }) => {
      const firstRect = first.getBoundingClientRect();
      const lastRect = last.getBoundingClientRect();
      const lastOffsetTop = lastRect.bottom - 40;
      const firstOffsetTop = firstRect.top - 140;

      const progressUp = firstOffsetTarget > 0 ? clamp01(scrollY / firstOffsetTarget) : 1;

      let progressDown = 0;
      if (lastOffsetTop <= windowHeight && lastOffsetTop >= 0) {
        progressDown = 1 - lastOffsetTop / windowHeight;
      } else if (lastOffsetTop < 0) {
        progressDown = 1;
      }

      const progress = progressDown > 0 ? 1 - progressDown : progressUp;
      const r = Math.round(startBgColor.r + (endBgColor.r - startBgColor.r) * progress);
      const g = Math.round(startBgColor.g + (endBgColor.g - startBgColor.g) * progress);
      const b = Math.round(startBgColor.b + (endBgColor.b - startBgColor.b) * progress);
      const color = `rgb(${r}, ${g}, ${b})`;

      if (color !== lastPaintedColor) {
        document.body.style.backgroundColor = color;
        lastPaintedColor = color;
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target === first && entry.isIntersecting && !firstTopTouched) {
            firstTopTouched = true;
            console.log('First project top minus 140px touched the top edge of the screen.');
          }

          if (entry.target === last && entry.isIntersecting && !lastBottomEnteredViewport) {
            lastBottomEnteredViewport = true;
            console.log('Last project bottom touched/entered the bottom edge of the viewport.');
          }
        });
      },
      { rootMargin: '140px 0px -40px 0px', threshold: 0 }
    );

    observer.observe(first);
    observer.observe(last);

    onScroll(updateBackground);
    onResize(() => {
      windowHeight = window.innerHeight;
      recalcTargets();
      requestScrollTick();
    });

    recalcTargets();
    requestScrollTick();
  }

  // Fade <hr> elements toward edges of viewport.
  const hrs = Array.from(document.querySelectorAll('hr'));
  if (hrs.length) {
    const hrData = hrs.map((hr) => {
      const initialOpacity = parseFloat(window.getComputedStyle(hr).opacity);
      return {
        el: hr,
        baseOpacity: Number.isFinite(initialOpacity) ? initialOpacity : 1,
      };
    });

    let viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    const clamp01 = (value) => Math.min(Math.max(value, 0), 1);

    const updateHrOpacity = ({ viewportHeight: nextViewportHeight }) => {
      viewportHeight = nextViewportHeight;
      const centerY = viewportHeight / 2;
      const maxDistance = centerY;

      const rects = hrData.map((item) => item.el.getBoundingClientRect());

      rects.forEach((rect, index) => {
        const hrCenter = (rect.top + rect.bottom) / 2;
        const distanceFromCenter = Math.abs(hrCenter - centerY);
        const t = clamp01(distanceFromCenter / maxDistance);
        const baseOpacity = hrData[index].baseOpacity;
        const opacity = baseOpacity * (1 - t);
        hrData[index].el.style.opacity = opacity.toFixed(3);
      });
    };

    onScroll(updateHrOpacity);
    onResize(({ viewportHeight: nextViewportHeight }) => {
      viewportHeight = nextViewportHeight;
      requestScrollTick();
    });

    requestScrollTick();
  }

  // Fade in the #inquire element as the first project nears the top.
  const inquire = document.getElementById('inquire');
  const firstProject = document.querySelector('.project');
  if (inquire && firstProject) {
    const activationOffset = 180;
    const rampRange = 80;
    let firstOffsetTarget = 0;
    let lastOpacity = '';

    const recalcTarget = () => {
      const rect = firstProject.getBoundingClientRect();
      firstOffsetTarget = Math.max(rect.top + window.scrollY - activationOffset, 0);
    };

    const updateOpacity = ({ scrollY }) => {
      const rampStart = firstOffsetTarget - rampRange;
      const raw = (scrollY - rampStart) / rampRange;
      const nextOpacity = Math.min(Math.max(raw, 0), 1).toFixed(3);
      if (nextOpacity !== lastOpacity) {
        inquire.style.opacity = nextOpacity;
        lastOpacity = nextOpacity;
      }
    };

    onScroll(updateOpacity);
    onResize(() => {
      recalcTarget();
      requestScrollTick();
    });

    recalcTarget();
    requestScrollTick();
  }

  // Fade the main canvas based on scroll; pause p5 when fully transparent.
  (() => {
    let canvasEl = document.querySelector('canvas');
    const fadeDistance = 200;
    const observerConfig = { childList: true, subtree: true };
    let isPaused = false;

    const setOpacity = ({ scrollY }) => {
      if (!canvasEl) return;
      const progress = Math.max(0, Math.min(scrollY / fadeDistance, 1));
      const opacity = 1 - progress;

      canvasEl.style.opacity = opacity;

      if (opacity <= 0.001 && !isPaused && typeof window.noLoop === 'function') {
        window.noLoop();
        isPaused = true;
      } else if (opacity > 0.001 && isPaused && typeof window.loop === 'function') {
        window.loop();
        isPaused = false;
      }
    };

    const updateCanvasRef = () => {
      const found = document.querySelector('canvas');
      if (!found || found === canvasEl) return;
      canvasEl = found;
      setOpacity(state);
    };

    const observer = new MutationObserver(() => {
      updateCanvasRef();
    });

    updateCanvasRef();
    setOpacity(state);
    onScroll(setOpacity);
    onResize(() => {
      setOpacity(state);
      updateCanvasRef();
    });
    observer.observe(document.body, observerConfig);
  })();
}

// Handle window resizing
function windowResized() {
  resizeCanvas(windowWidth, windowHeight);
  // Update the global center vector based on the golden ratio
  center.set(width * INV_PHI, height * (1 - INV_PHI));
  background(255, 255, 254);
}

// Boid class
class Boid {
  constructor() {
    this.position = createVector(
      randomGaussian(center.x, width / 8),
      randomGaussian(center.y, height / 8)
    );
    const angle = random(TWO_PI);
    this.velocity = p5.Vector.fromAngle(angle);
    this.velocity.setMag(random(1, MAX_SPEED));
    this.acceleration = createVector();

    // Preallocate vectors for performance
    this.alignment = createVector();
    this.cohesion = createVector();
    this.separation = createVector();
    this.gravitation = createVector();
    this.temp = createVector(); // General-purpose temporary vector
    this.diff = createVector(); // For separation calculations
  }

  // Apply flocking behavior
  flock(boids) {
    // Reset force accumulators
    this.alignment.set(0, 0);
    this.cohesion.set(0, 0);
    this.separation.set(0, 0);
    this.gravitation.set(0, 0);

    let totalAlign = 0;
    let totalCohere = 0;
    let totalSeparation = 0;

    // Precompute squared perception radii
    const perceptionRadiusSq = PERCEPTION_RADIUS_SQ;
    const separationRadiusSq = SEPARATION_RADIUS_SQ;

    // Reuse variables for distance calculations
    let dx, dy, distanceSq;

    for (let i = 0; i < boids.length; i++) {
      const other = boids[i];
      if (other === this) continue;

      dx = other.position.x - this.position.x;
      dy = other.position.y - this.position.y;
      distanceSq = dx * dx + dy * dy;

      if (distanceSq < perceptionRadiusSq) {
        // Alignment
        this.alignment.add(other.velocity);
        totalAlign++;

        // Cohesion
        this.cohesion.add(other.position);
        totalCohere++;
      }
      if (distanceSq < separationRadiusSq) {
        // Separation
        this.diff.set(this.position).sub(other.position);
        // Avoid division by zero
        if (distanceSq > 0) {
          this.diff.div(distanceSq);
        }
        this.separation.add(this.diff);
        totalSeparation++;
      }
    }

    // Alignment
    if (totalAlign > 0) {
      this.alignment.div(totalAlign);
      this.alignment.setMag(MAX_SPEED);
      this.alignment.sub(this.velocity);
      this.alignment.limit(MAX_FORCE);
    }

    // Cohesion
    if (totalCohere > 0) {
      this.cohesion.div(totalCohere);
      this.cohesion.sub(this.position);
      this.cohesion.setMag(MAX_SPEED);
      this.cohesion.sub(this.velocity);
      this.cohesion.limit(MAX_FORCE);
    }

    // Separation
    if (totalSeparation > 0) {
      this.separation.div(totalSeparation);
      this.separation.setMag(MAX_SPEED);
      this.separation.sub(this.velocity);
      this.separation.limit(MAX_FORCE);
    }

    // Gravitation
    this.gravitate();

    // Weight the forces
    this.alignment.mult(1.0);
    this.cohesion.mult(1.0);
    this.separation.mult(1.125);
    // this.gravitation is already scaled in gravitate()

    // Accumulate all forces
    this.acceleration.add(this.alignment);
    this.acceleration.add(this.cohesion);
    this.acceleration.add(this.separation);
    this.acceleration.add(this.gravitation); // Add center pull force
  }

  // Gravitation: Dynamically pull boid towards the center based on proportional distance
  gravitate() {
    // Calculate normalized distances along x and y axes
    let dx = abs(this.position.x - center.x) / (width / 2);
    let dy = abs(this.position.y - center.y) / (height / 2);

    // Use the maximum of the normalized distances
    let proportion = dx > dy ? dx : dy;
    proportion = proportion > 1 ? 1 : proportion; // Constrain proportion between 0 and 1

    // Adjust proportion so that full strength is reached at 61.8% of distance
    proportion = min(proportion / 0.618, 1);

    // Strength of the pull is proportional to the distance from the center
    let strength = proportion * MAX_PULL_FORCE;

    // Steering vector towards center
    this.temp.set(center.x - this.position.x, center.y - this.position.y);
    this.temp.normalize().mult(strength);
    this.gravitation.set(this.temp);
  }

  // Update position based on velocity and acceleration
  update() {
    this.velocity.add(this.acceleration);
    this.velocity.limit(MAX_SPEED);
    this.position.add(this.velocity);
    this.acceleration.mult(0);
  }

  // Display the boid as a point
  show() {
    point(this.position.x, this.position.y);
  }
}
